import { ApolloError, useMutation } from '@apollo/client'

import { Form, FormCreateInput, MutationCreateFormArgs, ProjectFile } from '@types'

import { MUTATION_CREATE_FORM } from '@libs/apollo/mutations/form.mutation'
import { PROJECT_FILE_WITH_POSITIONS } from '@libs/apollo/queries'
import { FORMS } from '@libs/apollo/queries/form.query'

interface Data {
  createForm?: Form
}

interface UseCreateFormProps {
  onCompleted?: () => void
}

export const useCreateForm = (
  props?: UseCreateFormProps,
): {
  createForm: (input: FormCreateInput) => void
  loading: boolean
  error: ApolloError | undefined
  data?: Data | null
} => {
  const [createForm, { loading, error, data }] = useMutation<Data, MutationCreateFormArgs>(MUTATION_CREATE_FORM, {
    notifyOnNetworkStatusChange: true,
  })

  const _createForm = async (input: FormCreateInput) => {
    try {
      await createForm({
        variables: { input },
        onCompleted: props?.onCompleted,
        update(cache, { data }) {
          if (!data?.createForm || !data.createForm?.projectFile?.id) return

          const existingData = cache.readQuery({
            query: PROJECT_FILE_WITH_POSITIONS,
            variables: { id: data.createForm?.projectFile.id },
          }) as { projectFile: ProjectFile }

          if (existingData?.projectFile) {
            cache.writeQuery({
              query: PROJECT_FILE_WITH_POSITIONS,
              variables: { id: data.createForm.projectFile.id },
              data: {
                projectFile: {
                  ...existingData.projectFile,
                  forms: [data.createForm, ...(existingData.projectFile.forms || [])],
                },
              },
            })
          }
        },
        refetchQueries: [
          {
            query: FORMS,
            variables: {
              projectId: input.projectId,
            },
          },
        ],
      })
    } catch (error) {
      console.error(error)
    }
  }

  return { createForm: _createForm, loading, error, data }
}
